import { Head } from '@inertiajs/react'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react'

import CollageGrid from '../../components/tools/CollageGrid'
import { type ToolAbout, ToolAboutSection } from '../../components/tools/toolChrome'
import {
  downloadCanvas,
  renderCollageToCanvas,
  waitForImagesReady,
  type ExportFormat,
} from '../../lib/collageExport'
import {
  clamp,
  DEFAULT_GRID_SETTINGS,
  MAX_GAP_PX,
  MIN_GAP_PX,
  type CollagePhoto,
  type GridSettings,
} from '../../lib/collageTypes'

const COLLAGE_ABOUT: ToolAbout = {
  description:
    "Arrange your photos into a compact grid. Each photo keeps its own aspect ratio. The tool never crops or distorts it. The tool solves each row's height so its photos exactly fill the available width. It also picks the row count that brings the whole collage closest to a square. Drag a photo to reorder it. Export the result as PNG, JPEG, or WebP. The tool reads each photo with URL.createObjectURL. Nothing is uploaded. The work stays in your browser.",
  libraries: [
    { name: 'MDN: Canvas API', href: 'https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API' },
    { name: 'MDN: Pointer events', href: 'https://developer.mozilla.org/en-US/docs/Web/API/Pointer_events' },
    {
      name: 'MDN: URL.createObjectURL()',
      href: 'https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL_static',
    },
  ],
}

const EXPORT_FORMATS: { id: ExportFormat; label: string }[] = [
  { id: 'png', label: 'PNG' },
  { id: 'jpeg', label: 'JPEG' },
  { id: 'webp', label: 'WebP' },
]

function loadImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('Could not read this image'))
    img.src = url
  })
}

export default function PhotoCollageTool() {
  const [photos, setPhotos] = useState<CollagePhoto[]>([])
  const [settings, setSettings] = useState<GridSettings>(DEFAULT_GRID_SETTINGS)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const [exportFormat, setExportFormat] = useState<ExportFormat>('png')
  const [exportQuality, setExportQuality] = useState(0.92)
  const [exportScale, setExportScale] = useState(2)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const tileElsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const imgElsRef = useRef<Map<string, HTMLImageElement>>(new Map())
  const photosRef = useRef<CollagePhoto[]>([])

  useEffect(() => {
    photosRef.current = photos
  }, [photos])

  // Object URLs are created outside React state; revoke every one still open when the page unmounts.
  useEffect(
    () => () => {
      for (const photo of photosRef.current) URL.revokeObjectURL(photo.url)
    },
    [],
  )

  const registerTileEl = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) tileElsRef.current.set(id, el)
    else tileElsRef.current.delete(id)
  }, [])

  const registerImgEl = useCallback((id: string, el: HTMLImageElement | null) => {
    if (el) imgElsRef.current.set(id, el)
    else imgElsRef.current.delete(id)
  }, [])

  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'))
    if (files.length === 0) return
    const loaded = await Promise.all(
      files.map(async (file): Promise<CollagePhoto | null> => {
        const url = URL.createObjectURL(file)
        try {
          const { width, height } = await loadImageDimensions(url)
          return {
            id: crypto.randomUUID(),
            file,
            url,
            naturalWidth: width,
            naturalHeight: height,
          }
        } catch {
          URL.revokeObjectURL(url)
          return null
        }
      }),
    )
    const newPhotos = loaded.filter((p): p is CollagePhoto => p !== null)
    if (newPhotos.length > 0) setPhotos((prev) => [...prev, ...newPhotos])
  }, [])

  const onFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) void addFiles(e.target.files)
      e.target.value = ''
    },
    [addFiles],
  )

  const onDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!e.dataTransfer.types.includes('Files')) return
    dragCounterRef.current += 1
    setIsDraggingFiles(true)
  }, [])

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  const onDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)
    if (dragCounterRef.current === 0) setIsDraggingFiles(false)
  }, [])

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragCounterRef.current = 0
      setIsDraggingFiles(false)
      if (e.dataTransfer.files?.length) void addFiles(e.dataTransfer.files)
    },
    [addFiles],
  )

  const removePhoto = useCallback((id: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id)
      if (target) URL.revokeObjectURL(target.url)
      return prev.filter((p) => p.id !== id)
    })
  }, [])

  const clearAll = useCallback(() => {
    setPhotos((prev) => {
      for (const photo of prev) URL.revokeObjectURL(photo.url)
      return []
    })
  }, [])

  const reorderPhotos = useCallback((fromIndex: number, toIndex: number) => {
    setPhotos((prev) => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }, [])

  const setGapPx = useCallback((gapPx: number) => {
    setSettings((prev) => ({ ...prev, gapPx: clamp(gapPx, MIN_GAP_PX, MAX_GAP_PX) }))
  }, [])

  const setBackgroundColor = useCallback((backgroundColor: string) => {
    setSettings((prev) => ({ ...prev, backgroundColor }))
  }, [])

  const handleExport = useCallback(async () => {
    if (!containerRef.current || photos.length === 0) return
    setExporting(true)
    setExportError(null)
    try {
      // clientWidth, not getBoundingClientRect().width: the container is CSS-scaled down for the
      // on-screen preview (see CollageGrid), and export resolution must stay independent of that.
      const containerWidth = containerRef.current.clientWidth
      await waitForImagesReady(imgElsRef.current)
      const canvas = renderCollageToCanvas({
        container: containerRef.current,
        photos,
        tileEls: tileElsRef.current,
        tileImgEls: imgElsRef.current,
        backgroundColor: settings.backgroundColor,
        outputWidth: containerWidth * exportScale,
      })
      await downloadCanvas(canvas, exportFormat, exportQuality, 'collage')
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Could not export the collage')
    } finally {
      setExporting(false)
    }
  }, [photos, settings.backgroundColor, exportFormat, exportQuality, exportScale])

  const showQuality = exportFormat !== 'png'
  const photoCountLabel = useMemo(
    () => `${photos.length} photo${photos.length === 1 ? '' : 's'}`,
    [photos.length],
  )

  return (
    <>
      <Head title="Photo collage" />
      <div className="space-y-8">
        <header className="flex flex-col justify-between gap-4 border-b border-outline-variant/20 pb-4 md:flex-row md:items-end">
          <div>
            <h1 className="font-headline text-3xl font-bold tracking-tighter text-on-surface uppercase">
              Photo collage
            </h1>
            <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-on-surface-variant uppercase">
              Status: {photoCountLabel} loaded // runs entirely in your browser
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={onFileInputChange}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-container px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-on-primary shadow-[0_4px_0_0_#9a4000] transition-all active:translate-y-0.5 active:shadow-none"
            >
              <span className="material-symbols-outlined text-[18px]" aria-hidden>
                add_photo_alternate
              </span>
              Add photos
            </button>
            {photos.length > 0 ? (
              <button
                type="button"
                onClick={clearAll}
                className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/35 bg-surface-container-high px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-on-surface transition-colors hover:border-error hover:text-error"
              >
                Clear all
              </button>
            ) : null}
          </div>
        </header>

        {photos.length > 0 ? (
          <div className="flex flex-wrap items-end gap-6 rounded-lg border border-outline-variant/20 bg-surface-container-low p-4">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                Spacing: {settings.gapPx}px
              </span>
              <input
                type="range"
                min={MIN_GAP_PX}
                max={MAX_GAP_PX}
                value={settings.gapPx}
                onChange={(e) => setGapPx(Number(e.target.value))}
                className="w-40"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                Background
              </span>
              <input
                type="color"
                value={settings.backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                aria-label="Background color"
                className="h-8 w-12 cursor-pointer rounded border border-outline-variant/40 bg-transparent p-0.5"
              />
            </label>
          </div>
        ) : null}

        <div
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`relative rounded-lg transition-colors ${
            isDraggingFiles ? 'outline-2 outline-dashed outline-primary-container' : ''
          }`}
        >
          {photos.length === 0 ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-outline-variant/40 bg-surface-container-low py-20 text-on-surface-variant transition-colors hover:border-primary-container hover:text-on-surface"
            >
              <span className="material-symbols-outlined text-5xl" aria-hidden>
                add_photo_alternate
              </span>
              <span className="font-mono text-xs font-bold uppercase tracking-widest">
                Drop photos here, or click to choose files
              </span>
            </button>
          ) : (
            <CollageGrid
              photos={photos}
              gapPx={settings.gapPx}
              backgroundColor={settings.backgroundColor}
              onReorder={reorderPhotos}
              onRemove={removePhoto}
              containerRef={containerRef}
              registerTileEl={registerTileEl}
              registerImgEl={registerImgEl}
            />
          )}
        </div>

        {photos.length > 0 ? (
          <div className="flex flex-wrap items-end gap-6 rounded-lg border border-outline-variant/20 bg-surface-container-low p-4">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                Format
              </span>
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                className="form-well max-w-[10rem] cursor-pointer py-1 pl-2 pr-7 font-mono text-xs text-on-surface"
              >
                {EXPORT_FORMATS.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            {showQuality ? (
              <label className="flex flex-col gap-1">
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                  Quality: {Math.round(exportQuality * 100)}%
                </span>
                <input
                  type="range"
                  min={0.4}
                  max={1}
                  step={0.01}
                  value={exportQuality}
                  onChange={(e) => setExportQuality(Number(e.target.value))}
                  className="w-32"
                />
              </label>
            ) : null}
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                Resolution: {exportScale}x
              </span>
              <input
                type="range"
                min={1}
                max={4}
                step={1}
                value={exportScale}
                onChange={(e) => setExportScale(Number(e.target.value))}
                className="w-32"
              />
            </label>
            <button
              type="button"
              disabled={exporting}
              onClick={() => void handleExport()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-container px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-on-primary shadow-[0_4px_0_0_#9a4000] transition-all active:translate-y-0.5 active:shadow-none disabled:cursor-wait disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-[18px]" aria-hidden>
                download
              </span>
              {exporting ? 'Exporting…' : 'Download'}
            </button>
          </div>
        ) : null}

        {exportError ? <p className="font-mono text-sm text-error">{exportError}</p> : null}

        <ToolAboutSection about={COLLAGE_ABOUT} />
      </div>
    </>
  )
}
